import { DsmPageContainer } from '@/components';
import PageHeader from '@/components/PageHeader';
import {
  type AvlAlias,
  type AvlItem,
  type AvlItemWritableFields,
  type NewAllocationDetailInput,
  useCreateAvlItemMutation,
  useGetAvlItemQuery,
  useGetAvlItemsQuery,
  useUpdateAvlItemMutation,
} from '@/services/approvedVendorList';
import { isErrorWithData } from '@/services/helpers';
import { PalmettoAutocomplete, PalmettoButton, PalmettoSelect, PalmettoTextInput } from '@palmetto/dsm-components';
import { toast } from '@palmetto/palmetto-components';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { useCallback, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import {
  ApprovedVendorAllocation,
  avlTimelineStart,
  CalculatorType,
  InverterType,
  ProgramType,
  VendorEquipmentTypes,
} from 'types';
import type { FeocDataItem } from 'types';

import { normalizeAliases } from './AliasEditor';
import {
  type PendingAllocationWindow,
  AllocationScheduleFormSection,
  isCompletePendingWindow,
  validatePendingWindows,
} from './AllocationScheduleFormSection';
import { sortByStartDateAsc } from './AllocationScheduleSection';
import { AvlItemFormFields, lockedHelperText } from './AvlItemFormFields';
import { VendorEquipmentTypeLabels } from './equipmentTypeLabels';

const equipmentTypeOptions = Object.values(VendorEquipmentTypes).map((value) => ({
  value,
  label: VendorEquipmentTypeLabels[value],
}));

export interface AvlFormValues {
  equipmentType: VendorEquipmentTypes | '';
  manufacturer: string;
  modelNumber: string;
  name: string;
  programTypes: ProgramType[];
  aliases: AvlAlias[];
  allocation: ApprovedVendorAllocation | '';
  needsApproval: boolean;
  sku: string;
  description: string;
  category: string;
  calculatorType: CalculatorType | '';
  inverterType: InverterType | '';
  domesticContentSupported: boolean;
  domesticContentPercentageMlpe: number | undefined;
  domesticContentPercentageString: number | undefined;
  feocPercentageMlpe: number | undefined;
  feocPercentageString: number | undefined;
  maxACkW: number | undefined;
  maxDCkW: number | undefined;
  maxPrice: number | undefined;
  requiresExtendedWarranty: boolean;
  extendedWarrantyCost: number | undefined;
  omnidianAlias: string;
  arbitrage: boolean;
  backup: boolean;
  bessDomesticContentPercentage: number | undefined;
  bessFeocPercentage: number | undefined;
  gridPairedACkW: number | undefined;
  gridPairedPeakACkW: number | undefined;
  usableCapacityKWh: number | undefined;
  size: { value: number | undefined; unit: 'kW' | 'kWh' | 'watts' } | undefined;
  feocData: Array<{
    vintage: string;
    subSku?: string;
    mlpeDcPercentage?: number;
    stringDcPercentage?: number;
    mlpeFeocPercentage?: number;
    stringFeocPercentage?: number;
    bessFeocPercentage?: number;
    bessDcPercentage?: number;
  }>;
  note: string;
  newAllocationDetails: PendingAllocationWindow[];
}

const emptyValues: AvlFormValues = {
  equipmentType: '',
  manufacturer: '',
  modelNumber: '',
  name: '',
  programTypes: [],
  aliases: [{ source: '', value: '' }],
  allocation: '',
  needsApproval: false,
  sku: '',
  description: '',
  category: '',
  calculatorType: '',
  inverterType: '',
  domesticContentSupported: false,
  domesticContentPercentageMlpe: undefined,
  domesticContentPercentageString: undefined,
  feocPercentageMlpe: undefined,
  feocPercentageString: undefined,
  maxACkW: undefined,
  maxDCkW: undefined,
  maxPrice: undefined,
  requiresExtendedWarranty: false,
  extendedWarrantyCost: undefined,
  omnidianAlias: '',
  arbitrage: false,
  backup: false,
  bessDomesticContentPercentage: undefined,
  bessFeocPercentage: undefined,
  gridPairedACkW: undefined,
  gridPairedPeakACkW: undefined,
  usableCapacityKWh: undefined,
  size: undefined,
  feocData: [],
  note: '',
  newAllocationDetails: [],
};

export const ApprovedVendorItemForm = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;

  const { data: existing, isLoading: isLoadingExisting } = useGetAvlItemQuery(id ?? skipToken);
  const [createItem, { isLoading: isCreating }] = useCreateAvlItemMutation();
  const [updateItem, { isLoading: isUpdating }] = useUpdateAvlItemMutation();

  const [aliasError, setAliasError] = useState<string | undefined>(undefined);
  const [allocationDetailsError, setAllocationDetailsError] = useState<string | undefined>(undefined);

  const prefillValues = useMemo<AvlFormValues | undefined>(
    () =>
      existing
        ? {
            equipmentType: existing.equipmentType,
            manufacturer: existing.manufacturer,
            modelNumber: existing.modelNumber,
            name: existing.name,
            programTypes: existing.programTypes ?? [],
            aliases: existing.aliases?.length ? existing.aliases : [{ source: '', value: '' }],
            allocation: existing.allocation,
            needsApproval: existing.needsApproval ?? false,
            sku: existing.sku ?? '',
            description: existing.description ?? '',
            category: existing.category ?? '',
            calculatorType: existing.calculatorType ?? '',
            inverterType: existing.inverterType ?? '',
            domesticContentSupported: existing.domesticContentSupported ?? false,
            domesticContentPercentageMlpe: existing.domesticContentPercentageMlpe,
            domesticContentPercentageString: existing.domesticContentPercentageString,
            feocPercentageMlpe: existing.feocPercentageMlpe,
            feocPercentageString: existing.feocPercentageString,
            maxACkW: existing.maxACkW,
            maxDCkW: existing.maxDCkW,
            maxPrice: existing.maxPrice,
            requiresExtendedWarranty: existing.requiresExtendedWarranty ?? false,
            extendedWarrantyCost: existing.extendedWarrantyCost,
            omnidianAlias: existing.omnidianAlias ?? '',
            arbitrage: existing.arbitrage ?? false,
            backup: existing.backup ?? false,
            bessDomesticContentPercentage: existing.bessDomesticContentPercentage,
            bessFeocPercentage: existing.bessFeocPercentage,
            gridPairedACkW: existing.gridPairedACkW,
            gridPairedPeakACkW: existing.gridPairedPeakACkW,
            usableCapacityKWh: existing.usableCapacityKWh,
            size: existing.size ?? undefined,
            feocData: existing.feocData ?? [],
            note: '',
            newAllocationDetails: [],
          }
        : undefined,
    [existing],
  );

  const { control, handleSubmit, watch } = useForm<AvlFormValues>({
    defaultValues: emptyValues,
    values: prefillValues,
  });

  const selectedEquipmentType = watch('equipmentType');
  const selectedManufacturer = watch('manufacturer');

  const { data: avlItemsData } = useGetAvlItemsQuery(
    selectedEquipmentType ? { equipmentType: selectedEquipmentType, limit: 1000 } : skipToken,
  );

  const manufacturerSuggestions = useMemo(() => {
    if (!avlItemsData?.data) return [];
    const seen = new Set<string>();
    const manufacturers: string[] = [];
    for (const item of avlItemsData.data) {
      const mfg = item.manufacturer.trim();
      if (mfg && !seen.has(mfg.toLowerCase())) {
        seen.add(mfg.toLowerCase());
        manufacturers.push(mfg);
      }
    }
    return manufacturers.toSorted((a, b) => a.localeCompare(b)).map((m) => ({ value: m, label: m }));
  }, [avlItemsData]);

  // Ark's Combobox resolves the controlled `value` against its collection (built from `items`)
  // by exact string. Guarantee the current manufacturer is always a selectable option — injected
  // ahead of the async suggestions and preserving its stored value — so the selection shows on load.
  // Suggestion matching is trim/case-insensitive so a stored value differing only by whitespace/case
  // isn't duplicated.
  const manufacturerItems = useMemo(() => {
    const current = selectedManufacturer?.trim();
    const items = manufacturerSuggestions.map((opt) => ({
      ...opt,
      selected: !!current && opt.value.trim().toLowerCase() === current.toLowerCase(),
    }));
    if (selectedManufacturer && !items.some((opt) => opt.value.trim().toLowerCase() === current!.toLowerCase())) {
      // Preserve the item's actual stored value (not the trimmed form) so `value` matches exactly.
      return [{ value: selectedManufacturer, label: selectedManufacturer, selected: true }, ...items];
    }
    return items;
  }, [manufacturerSuggestions, selectedManufacturer]);

  const isLoading = isCreating || isUpdating || isLoadingExisting;
  const fieldsDisabled = isLoading || !selectedEquipmentType;

  // Tier 2 identity fields are immutable once set server-side. On edit, lock any that already
  // hold a non-empty stored value; empty optional fields (sku/category) stay editable until filled.
  const lockedFields = useMemo<ReadonlySet<string>>(() => {
    if (!existing) return new Set();
    const tier2: (keyof AvlItem)[] = ['equipmentType', 'manufacturer', 'modelNumber', 'sku', 'category'];
    const locked = tier2.filter((name) => String(existing[name] ?? '').trim() !== '');
    // allocation is edit-only via the Allocation Schedule below, once the item exists.
    return new Set([...locked, 'allocation']);
  }, [existing]);

  // Floor must match the server's seeded avlTimelineStart row, not undefined, for an unmigrated item.
  const existingLatestStartDate = useMemo(() => {
    const activeRows = sortByStartDateAsc((existing?.allocationDetails ?? []).filter((row) => !row.archived));
    return activeRows.length > 0 ? activeRows[activeRows.length - 1].startDate : avlTimelineStart;
  }, [existing]);

  const isEquipmentTypeLocked = lockedFields.has('equipmentType');
  const isManufacturerLocked = lockedFields.has('manufacturer');

  const onSubmit = useCallback(
    async (values: AvlFormValues) => {
      setAliasError(undefined);
      setAllocationDetailsError(undefined);

      if (!values.equipmentType) {
        toast.error('Equipment type is required');
        return;
      }
      if (!values.allocation) {
        toast.error('Allocation is required');
        return;
      }

      // All-or-nothing, like the server: one bad pending window blocks the whole save.
      const pendingWindowErrors = validatePendingWindows(existingLatestStartDate, values.newAllocationDetails);
      if (pendingWindowErrors.some(Boolean)) {
        toast.error('Fix the allocation window errors before saving.');
        return;
      }
      const newAllocationDetails: NewAllocationDetailInput[] =
        values.newAllocationDetails.filter(isCompletePendingWindow);

      // Every optional field below is always present in the payload — as its value, or `null` when
      // cleared — never omitted. The server can't tell "leave this alone" apart from "the user
      // cleared it" unless a cleared field is sent explicitly; an omitted key is a silent no-op on
      // the server's partial `$set` and the old value would persist forever.
      const feocDataNormalized: FeocDataItem[] = values.feocData.map((row) => ({
        vintage: row.vintage,
        ...(row.subSku ? { subSku: row.subSku } : {}),
        ...(row.mlpeDcPercentage !== undefined ? { mlpeDcPercentage: row.mlpeDcPercentage } : {}),
        ...(row.stringDcPercentage !== undefined ? { stringDcPercentage: row.stringDcPercentage } : {}),
        ...(row.mlpeFeocPercentage !== undefined ? { mlpeFeocPercentage: row.mlpeFeocPercentage } : {}),
        ...(row.stringFeocPercentage !== undefined ? { stringFeocPercentage: row.stringFeocPercentage } : {}),
        ...(row.bessFeocPercentage !== undefined ? { bessFeocPercentage: row.bessFeocPercentage } : {}),
        ...(row.bessDcPercentage !== undefined ? { bessDcPercentage: row.bessDcPercentage } : {}),
      }));

      // `allocation` is create-only — PUT 400s if it's present once the item exists.
      const payload: AvlItemWritableFields = {
        equipmentType: values.equipmentType,
        manufacturer: values.manufacturer.trim(),
        modelNumber: values.modelNumber.trim(),
        name: values.name.trim(),
        programTypes: values.programTypes,
        aliases: normalizeAliases(values.aliases),
        ...(isEdit ? {} : { allocation: values.allocation }),
        needsApproval: values.needsApproval,
        sku: values.sku.trim() || null,
        description: values.description.trim() || null,
        category: values.category.trim() || null,
        calculatorType: values.calculatorType || null,
        inverterType: values.inverterType || null,
        domesticContentSupported: values.domesticContentSupported,
        domesticContentPercentageMlpe: values.domesticContentPercentageMlpe ?? null,
        domesticContentPercentageString: values.domesticContentPercentageString ?? null,
        feocPercentageMlpe: values.feocPercentageMlpe ?? null,
        feocPercentageString: values.feocPercentageString ?? null,
        maxACkW: values.maxACkW ?? null,
        maxDCkW: values.maxDCkW ?? null,
        maxPrice: values.maxPrice ?? null,
        requiresExtendedWarranty: values.requiresExtendedWarranty,
        extendedWarrantyCost: values.extendedWarrantyCost ?? null,
        omnidianAlias: values.omnidianAlias.trim() || null,
        arbitrage: values.arbitrage,
        backup: values.backup,
        bessDomesticContentPercentage: values.bessDomesticContentPercentage ?? null,
        bessFeocPercentage: values.bessFeocPercentage ?? null,
        gridPairedACkW: values.gridPairedACkW ?? null,
        gridPairedPeakACkW: values.gridPairedPeakACkW ?? null,
        usableCapacityKWh: values.usableCapacityKWh ?? null,
        size: values.size?.value !== undefined ? { value: values.size.value, unit: values.size.unit } : null,
        feocData: feocDataNormalized,
      };

      try {
        let saved;
        if (isEdit) {
          saved = await updateItem({
            id: id!,
            data: {
              ...payload,
              modifiedAt: existing?.meta?.modifiedAt ?? null,
              ...(values.note.trim() ? { note: values.note.trim() } : {}),
              ...(newAllocationDetails.length > 0 ? { newAllocationDetails } : {}),
            },
          }).unwrap();
          toast.success('Item updated successfully');
        } else {
          saved = await createItem(payload).unwrap();
          toast.success('Item created successfully');
        }
        navigate(`/admin/approved-vendors/${saved.id}`);
      } catch (error) {
        console.error('Error saving approved vendor item:', error);
        if (isErrorWithData(error)) {
          const message = error.data.message;
          if (/alias/i.test(message)) {
            setAliasError(message);
          }
          if (/window/i.test(message)) {
            setAllocationDetailsError(message);
          }
          toast.error(message);
        } else {
          toast.error('Failed to save item. Please try again.');
        }
      }
    },
    [createItem, updateItem, isEdit, id, navigate, existingLatestStartDate, existing],
  );

  const handleCancel = useCallback(() => navigate('/admin/approved-vendors'), [navigate]);

  const pageTitle = isEdit ? `Edit: ${existing?.name ?? 'Loading...'}` : 'New Equipment';

  return (
    <DsmPageContainer title={pageTitle}>
      <PageHeader title={pageTitle} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="mb-16 grid grid-cols-2 gap-x-16 gap-y-8">
          <Controller
            name="equipmentType"
            control={control}
            render={({ field }) => (
              <PalmettoSelect
                label="Equipment Type"
                items={equipmentTypeOptions.map((opt) => ({ ...opt, selected: opt.value === field.value }))}
                onValueChange={(details: { value: string[] }) => field.onChange(details.value[0] ?? '')}
                disabled={isEquipmentTypeLocked || isLoading}
                helperText={isEquipmentTypeLocked ? lockedHelperText : undefined}
              />
            )}
          />
          <Controller
            name="manufacturer"
            control={control}
            rules={{ required: 'Manufacturer is required' }}
            render={({ field, fieldState }) => (
              <PalmettoAutocomplete
                // PalmettoAutocomplete (unlike PalmettoSelect) only reads `items`/`selected` to seed
                // its displayed value on mount and never re-syncs afterward. `existing` loads
                // asynchronously and react-hook-form's `values` sync (and therefore `watch`) lags
                // that by a render, so without this the field mounts blank and stays blank once the
                // real manufacturer arrives. Remount once `selectedManufacturer` has actually caught
                // up to the stored value, so the fresh mount's `items` already carries the
                // selection. Manufacturer is locked (immutable) once non-empty, so this only fires
                // once per edit session and never interferes with typing a new value on create.
                key={isEdit && selectedManufacturer !== (existing?.manufacturer ?? '') ? 'pending' : 'ready'}
                id="manufacturer"
                label="Manufacturer"
                items={manufacturerItems}
                value={field.value ? [field.value] : []}
                onValueChange={(details: { value: string[] }) => field.onChange(details.value[0] ?? '')}
                onInputValueChange={(details: { inputValue: string }) => {
                  if (details.inputValue) {
                    field.onChange(details.inputValue);
                  }
                }}
                placeholder="Type or select a manufacturer"
                helperText={isManufacturerLocked ? lockedHelperText : undefined}
                validationMessage={
                  fieldState.error ? { type: 'error' as const, text: fieldState.error.message ?? '' } : undefined
                }
                disabled={fieldsDisabled || isManufacturerLocked}
              />
            )}
          />
        </div>

        <AvlItemFormFields
          equipmentType={selectedEquipmentType}
          control={control}
          disabled={fieldsDisabled}
          lockedFields={lockedFields}
          aliasError={aliasError}
        />

        {isEdit && (
          <AllocationScheduleFormSection
            control={control}
            existingAllocationDetails={existing?.allocationDetails}
            existingLatestStartDate={existingLatestStartDate}
            disabled={isLoading}
            serverError={allocationDetailsError}
          />
        )}

        {isEdit && (
          <div className="mb-16">
            <Controller
              name="note"
              control={control}
              render={({ field }) => (
                <PalmettoTextInput
                  id="note"
                  label="Change Note"
                  helperText="Optional note describing this change (recorded in history)"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  disabled={isLoading}
                />
              )}
            />
          </div>
        )}

        <div className="flex justify-end gap-8">
          <PalmettoButton
            type="button"
            emphasis="secondary"
            layout="hug"
            onClick={handleCancel}
            disabled={isLoading}
          >
            Cancel
          </PalmettoButton>
          <PalmettoButton type="submit" emphasis="primary" layout="hug" disabled={fieldsDisabled}>
            {isLoading ? 'Saving...' : 'Save Item'}
          </PalmettoButton>
        </div>
      </form>
    </DsmPageContainer>
  );
};
